<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.country.countries') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('settings.backToSettings') }}
        </d-button>
        <d-button theme="success" @click.prevent="$router.push('/ustawienia/kraje/nowy')">
          <i class="material-icons">add</i>
          {{ $t('settings.country.addNewCountry') }}
        </d-button>
      </d-col>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('settings.country.countryName') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.country.countryInvoiceCode') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.country.countryAddressCode') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.country.EU') }}</th>
            <th scope="col" class="border-0 text-center">{{ $t('settings.country.taxNumberCheck') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.language') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(country, index) of countries"
            :key="'country-' + index">
            <td class="align-middle">
              {{ country.name }}
            </td>
            <td class="align-middle">
              {{ country.code }}
            </td>
            <td class="align-middle">
              {{ country.address_code }}
            </td>
            <td class="align-middle">
              <span
                v-if="!country.is_eu"
                class="text-danger">
                {{ $t('ui.no') }}
              </span>
              <span
                v-else
                class="text-success">
                {{ $t('ui.yes') }}
              </span>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!country.verify_tax_number"
                class="text-danger">
                {{ $t('ui.no') }}
              </span>
              <span
                v-else
                class="text-success">
                {{ $t('ui.yes') }}
              </span>
            </td>
            <td class="align-middle">
              <template v-if="country.default_language_id > 0">
                {{ languageNames[country.default_language_id] }}
              </template>
              <template v-else>
                &mdash;
              </template>
            </td>
            <td class="align-middle">
              <template v-if="country.default_currency_id > 0">
                {{ currencyNames[country.default_currency_id] }}
              </template>
              <template v-else>
                &mdash;
              </template>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/ustawienia/kraje/edycja/' + country.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCountry(country.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="countries.length === 0">
            <td
              colspan="3"
              class="text-center">
              {{ $t('settings.country.noCountriesToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>
  </d-container>
</template>

<script>
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';

export default {
  name: 'countries',
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies && this.loadedLanguages;
    },
    currencyNames () {
      let output = [];

      for (let i = 0; i < this.currencies.length; i++) {
        let currency = this.currencies[i];
        output[currency.id] = currency.code;
      }

      return output;
    },
    languageNames () {
      let output = [];

      for (let i = 0; i < this.languages.length; i++) {
        let language = this.languages[i];
        output[language.id] = language.name;
      }

      return output;
    }
  },
  data() {
    return {
      countries: [],
      currencies: [],
      languages: [],
      loadedCurrencies: false,
      loadedLanguages: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    };
  },
  mounted () {
    this.loadData();
    this.loadAdditionalData();
  },
  methods: {
    loadData () {
      ListUtils.loadItemsData(this, '/countries/items', 'countries', {}, 'get');
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    removeCountry (countryID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/countries/delete/',
        id: countryID,
        successTitle: this.$t('settings.country.removeCountrySuccessTitle'),
        successText: this.$t('settings.country.removeCountrySuccessText'),
        confirmTitle: this.$t('settings.country.removeCountryConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.country.removeCountryErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

